/**
 * Rental pricing.
 *
 * Rates come in three tiers (1 day / 3 days / 1 week). For any rental, we take
 * the largest tier that fits within the number of days, derive its per-day
 * rate, and charge that rate for every day.
 *
 *   equipment trailer, 5 days  → 3-day tier $300 → $100/day → $500
 *   dump trailer,      5 days  → 3-day tier $345 → $115/day → $575
 *   dump trailer,      8 days  → week tier $725  → $103.57/day → $829
 *
 * The effect is that once a customer crosses a tier, the whole rental is
 * priced at that better rate rather than a block plus day-rate stragglers.
 */

/**
 * Billable days between two ISO dates, inclusive of both.
 * Drop Monday, pick up Wednesday = 3 days.
 */
export function billableDays(startISO, endISO) {
  if (!startISO || !endISO) return 0
  const start = new Date(`${startISO}T00:00:00`)
  const end = new Date(`${endISO}T00:00:00`)
  const ms = end - start
  if (ms < 0) return 0
  return Math.round(ms / 86400000) + 1
}

/**
 * Returns { total, perDay, tier, breakdown }.
 * breakdown keeps the same shape the quote panel renders.
 */
export function priceRental(trailer, days) {
  if (!trailer || days < 1) {
    return { total: 0, perDay: 0, tier: null, breakdown: [] }
  }

  // Ascending by span so the last match is the largest tier that fits.
  const tiers = [
    { label: 'day rate', span: 1, rate: trailer.dayRate },
    { label: '3-day rate', span: 3, rate: trailer.threeDayRate },
    { label: 'weekly rate', span: 7, rate: trailer.weekRate },
  ].filter((t) => typeof t.rate === 'number')

  const fitting = tiers.filter((t) => t.span <= days)
  const tier = fitting.length > 0 ? fitting[fitting.length - 1] : tiers[0]

  const perDay = tier.rate / tier.span
  const total = Math.round(perDay * days)

  return {
    total,
    perDay: Math.round(perDay),
    tier: tier.label,
    breakdown: [
      {
        label: `${days} day${days > 1 ? 's' : ''} at ${tier.label}`,
        each: Math.round(perDay),
        count: days,
        subtotal: total,
      },
    ],
  }
}

export function formatUSD(amount) {
  return `$${amount.toLocaleString('en-US')}`
}
